import { useEffect, useState, type ReactNode } from 'react';
import { BarChart3 } from 'lucide-react';
import { formatNumber } from '../../utils/formatters';
import { formatarNome } from '../../utils/nomes';
import { useAuth } from '../../hooks/useAuth';
import { useUsageStats } from '../../hooks/useUsageStats';
import { GraficoDeMontanha } from './GraficoDeMontanha';
import { RECURSO_CERTIFICADOS, temRecurso } from '../../utils/features';
import {
  PERIODOS_EM_DIAS,
  PERIODO_PADRAO,
  nomeDoTitular,
  podeVerEstatisticas,
  textoDoCorte,
  textoDoVencimento,
  usosPorExtenso,
} from '../../utils/usageStats';

const cartao = {
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-md)',
} as const;

function Numero({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="glass" style={cartao}>
      <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatNumber(valor)}</div>
      <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>{rotulo}</div>
    </div>
  );
}

function Lista({ titulo, vazio, nota, children }: { titulo: string; vazio: string; nota?: string | null; children: ReactNode[] }) {
  // M4: sem itens, o cartão vira uma linha só (título e a frase), sem esticar até a altura da fileira.
  if (children.length === 0) {
    return (
      <div className="glass" style={{ ...cartao, padding: 'var(--space-4) var(--space-5)' }}>
        <h4 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>{titulo}</h4>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>{vazio}</p>
      </div>
    );
  }
  return (
    <div className="glass" style={cartao}>
      <h4 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-3)' }}>{titulo}</h4>
      {(
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {children}
        </ul>
      )}
      {nota && <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs, 12px)', marginTop: 'var(--space-3)' }}>{nota}</p>}
    </div>
  );
}

function Linha({ texto, detalhe, valor }: { texto: string; detalhe?: string | null; valor?: string }) {
  return (
    <li style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {texto}
        {detalhe && <span style={{ color: 'var(--color-text-secondary)' }}> · {detalhe}</span>}
      </span>
      {valor && <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{valor}</span>}
    </li>
  );
}

/**
 * Cartões de uso do cofre no dashboard (Etapa 19). Só owner e admin: para member o componente não
 * aparece e nem chama o servidor (que responderia 403).
 */
export function UsageKpis() {
  const { membership } = useAuth();
  const permitido = podeVerEstatisticas(membership?.role);
  const comCertificados = temRecurso(membership, RECURSO_CERTIFICADOS);
  const { stats, isLoading, error, fetchStats } = useUsageStats();
  const [dias, setDias] = useState<number>(PERIODO_PADRAO);
  // Pessoa escolhida no gráfico: filtra as credenciais (quantas vezes ela usou cada uma).
  const [pessoa, setPessoa] = useState<string | null>(null);

  useEffect(() => {
    if (permitido) fetchStats(dias, pessoa);
  }, [permitido, dias, pessoa, fetchStats]);

  if (!permitido) return null;

  return (
    <section className="glass animate-scale-in" style={{ ...cartao, marginTop: 'var(--space-8)', padding: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <BarChart3 size={22} style={{ color: 'var(--color-primary-400)' }} /> Uso do cofre
        </h3>
        <div role="group" aria-label="Período" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {PERIODOS_EM_DIAS.map((periodo) => (
            <button
              key={periodo}
              type="button"
              aria-pressed={dias === periodo}
              onClick={() => setDias(periodo)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-default)',
                background: dias === periodo ? 'var(--color-primary-600)' : 'var(--color-bg-elevated)',
                color: dias === periodo ? 'var(--color-primary-50)' : 'var(--color-text-primary)',
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
              }}
            >
              {periodo} dias
            </button>
          ))}
        </div>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-error, #ef4444)', fontSize: 'var(--text-sm)' }}>Não foi possível carregar as estatísticas.</p>}
      {/* M4: esqueleto no lugar do "Carregando...", sem salto de layout quando os dados chegam */}
      {!stats && !error && (
        <div aria-busy="true" aria-label="Carregando estatísticas" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-4)' }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton esqueleto-bloco" />)}
        </div>
      )}

      {stats && (
        <div style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity var(--duration-fast)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <Numero rotulo="Usos de credenciais" valor={stats.total_uses} />
            <Numero rotulo="Pessoas ativas" valor={stats.active_people} />
            {comCertificados && <Numero rotulo="Assinaturas" valor={stats.signatures} />}
          </div>

          {/* Os dois gráficos de área primeiro; os demais cartões ficam abaixo deles. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <GraficoDeMontanha
              titulo="Quem mais usa"
              nomeDoItem="pessoas"
              vazio="Nenhum uso no período."
              pontos={stats.top_people.map((p) => ({ id: p.user_id, rotulo: formatarNome(p.name) || p.name, valor: p.uses }))}
              selecionada={pessoa}
              onSelecionar={setPessoa}
            />
            <GraficoDeMontanha
              titulo="Credenciais mais usadas"
              nomeDoItem="credenciais"
              sufixo={formatarNome(stats.top_people.find((p) => p.user_id === pessoa)?.name) || null}
              vazio="Nenhum uso no período."
              pontos={stats.top_credentials.map((c) => ({ id: c.credential_id, rotulo: c.label, detalhe: c.holder_name, valor: c.uses }))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 'var(--space-4)', alignItems: 'start' }}>
            <Lista
              titulo="Credenciais sem uso no período"
              vazio="Todas as credenciais foram usadas no período."
              nota={textoDoCorte(stats.unused_credentials.length, stats.unused_credentials_total)}
            >
              {stats.unused_credentials.map((c) => (
                <Linha key={c.credential_id} texto={c.label} detalhe={nomeDoTitular(c.holder_name)} />
              ))}
            </Lista>
            <Lista titulo="Usos por titular" vazio="Nenhum uso no período.">
              {stats.uses_by_holder.map((t) => (
                <Linha key={t.holder_id ?? 'sem-titular'} texto={nomeDoTitular(t.holder_name)} valor={usosPorExtenso(t.uses)} />
              ))}
            </Lista>
            {comCertificados && (
              <Lista titulo="Certificados vencendo em 30 dias" vazio="Nenhum certificado vence nos próximos 30 dias.">
                {stats.certificates_expiring.map((c) => (
                  <Linha key={c.certificate_id} texto={c.label} valor={textoDoVencimento(c.days_to_expire)} />
                ))}
              </Lista>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
